import { useContentfulLiveUpdates } from "@contentful/live-preview/react";
import classNames from "classnames";
import { Entry } from "contentful";
import dynamic from "next/dynamic";
import React, { CSSProperties } from "react";

import BreakpointAwareAsset from "~/components/common/breakpoint-aware-asset";
import EditorialEntry from "~/components/editorials/editorial-entry";
import Editorials from "~/types/editorials";
import { assertEditorialType, isActiveEntry } from "~/utils/editorial-utils";

import styles from "./editorial-element-plp.module.scss";

const EditorialCardPLP = dynamic(() => import("./editorial-card-plp"));
const ServicesCarouselPLP = dynamic(() => import("./services-carousel-plp"));

export function WrapperEditorialElementPLP({
  children,
  item,
  countEditorialElement,
}: {
  countEditorialElement: number;
  children: JSX.Element;
  item: Entry<Editorials.GridElement>;
}) {
  return (
    <div
      id={`editorial-element-${countEditorialElement}`}
      className={styles.wrapperEditorialElement}
      style={
        {
          "--number-columns-mobile": 6 * item.fields.numberColumnsMobile,
          "--number-columns-desktop": 3 * item.fields.numberColumnsDesktop,
        } as CSSProperties
      }
    >
      {children}
    </div>
  );
}

type Props = {
  editorialElement: Entry<unknown>;
  countEditorialElement: number;
};

export default function EditorialElementPLP({ editorialElement, countEditorialElement }: Props) {
  assertEditorialType<Editorials.GridElement>(editorialElement, "gridElement");
  const { component, numberColumnsMobile, numberColumnsDesktop } = editorialElement.fields;
  const liveEntry = useContentfulLiveUpdates(component);

  if (!isActiveEntry(component)) {
    return null;
  }

  switch (liveEntry?.sys.contentType?.sys.id) {
    //special cases
    case "card":
      return (
        <WrapperEditorialElementPLP item={editorialElement} countEditorialElement={countEditorialElement}>
          <EditorialCardPLP entry={liveEntry} numberColumnsDesktop={numberColumnsDesktop} />
        </WrapperEditorialElementPLP>
      );
    case "servicesCarousel":
      return (
        <WrapperEditorialElementPLP item={editorialElement} countEditorialElement={countEditorialElement}>
          <ServicesCarouselPLP
            entry={liveEntry}
            numberColumnsMobile={numberColumnsMobile}
            numberColumnsDesktop={numberColumnsDesktop}
          />
        </WrapperEditorialElementPLP>
      );
    case "breakpointAwareAsset":
      assertEditorialType<Editorials.ContentAwareAsset>(liveEntry, "breakpointAwareAsset");
      return (
        <WrapperEditorialElementPLP item={editorialElement} countEditorialElement={countEditorialElement}>
          <div
            className={classNames(
              styles.media,
              numberColumnsMobile === 2 ? styles.maxContentMobile : undefined,
              numberColumnsDesktop === 4 ? styles.maxContentDesktop : undefined
            )}
          >
            <BreakpointAwareAsset entry={component} />
          </div>
        </WrapperEditorialElementPLP>
      );

    default: {
      //standard cases
      return (
        <WrapperEditorialElementPLP item={editorialElement} countEditorialElement={countEditorialElement}>
          <div className="EditorialElementPLPContainer">
            <EditorialEntry entry={component} />
          </div>
        </WrapperEditorialElementPLP>
      );
    }
  }
}
